import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { format } from "date-fns";
import { Plus, ClipboardList, Archive, CalendarIcon } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/PageHeader";
import { DataTable } from "@/components/DataTable";
import { DateCell } from "@/components/DateCell";
import { StatusBadge } from "@/components/StatusBadge";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { FormDrawer } from "@/components/ui/form-drawer";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { useAuth } from "@/lib/auth-context";
import { api } from "@/lib/api";

interface PurchaseOrder {
  id: string;
  po_number: string;
  vendor_id: string;
  status: string;
  total: string;
  expected_date: string | null;
  order_date: string;
  created_at: string;
  vendor?: { name: string };
}

interface Vendor {
  id: string;
  name: string;
  code: string | null;
  status: string;
}

interface Account {
  id: string;
  name: string;
}

const poStatusOptions = [
  { value: "draft", label: "Draft" },
  { value: "submitted", label: "Submitted" },
  { value: "acknowledged", label: "Acknowledged" },
  { value: "confirmed", label: "Confirmed" },
  { value: "in_production", label: "In Production" },
  { value: "partially_received", label: "Partially Received" },
  { value: "received", label: "Received" },
  { value: "closed", label: "Closed" },
  { value: "cancelled", label: "Cancelled" },
  { value: "issue", label: "Issue" },
];

const newPoFormSchema = z.object({
  vendorId: z.string().min(1, "Please select a vendor"),
  shipToAccountId: z.string().optional(),
  orderDate: z.date(),
  notes: z.string().optional(),
});

type NewPoFormData = z.infer<typeof newPoFormSchema>;

export function PurchaseOrdersPage() {
  const { sessionId } = useAuth();
  const [, setLocation] = useLocation();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [newPoDrawerOpen, setNewPoDrawerOpen] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter, includeArchived]);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['purchase-orders', sessionId, search, currentPage, pageSize, statusFilter, includeArchived],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: pageSize.toString(),
      });
      if (search) params.append('search', search);
      if (statusFilter) params.append('status', statusFilter);
      if (includeArchived) params.append('includeArchived', 'true');
      
      const result = await api.get<{ purchaseOrders: PurchaseOrder[]; total: number }>(
        `/purchase-orders?${params}`,
        sessionId
      );
      return result;
    },
    enabled: !!sessionId,
  });

  const { data: vendorsData, isLoading: vendorsLoading } = useQuery({
    queryKey: ['vendors', sessionId],
    queryFn: async () => {
      const result = await api.get<{ vendors: Vendor[] }>('/vendors', sessionId);
      return result.vendors;
    },
    enabled: !!sessionId && newPoDrawerOpen,
  });
  const vendors = (vendorsData || []).filter((v) => v.status === "active");

  const { data: accountsData, isLoading: accountsLoading } = useQuery({
    queryKey: ['accounts', sessionId],
    queryFn: async () => {
      const result = await api.get<{ accounts: Account[] }>('/accounts', sessionId);
      return result.accounts;
    },
    enabled: !!sessionId && newPoDrawerOpen,
  });
  const accounts = accountsData || [];

  const form = useForm<NewPoFormData>({
    resolver: zodResolver(newPoFormSchema),
    defaultValues: {
      vendorId: "",
      shipToAccountId: undefined,
      orderDate: new Date(),
      notes: "",
    },
  });

  const createMutation = useMutation({
    mutationFn: async (formData: NewPoFormData) => {
      return await api.post('/purchase-orders', {
        vendorId: formData.vendorId,
        shipToAccountId: formData.shipToAccountId || null,
        orderDate: formData.orderDate.toISOString(),
        notes: formData.notes || null,
        status: "draft",
        currencyCode: "USD",
      }, sessionId);
    },
    onSuccess: (po: any) => {
      refetch();
      toast({
        title: "Purchase order created",
        description: "Redirecting to add line items...",
      });
      setNewPoDrawerOpen(false);
      form.reset({ vendorId: "", shipToAccountId: undefined, orderDate: new Date(), notes: "" });
      setLocation(`/purchase-orders/${po.id}`);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create purchase order",
        variant: "destructive",
      });
    },
  });

  const onSubmit = (formData: NewPoFormData) => {
    createMutation.mutate(formData);
  };

  const columns = [
    {
      key: "created_at",
      header: "Entry",
      sortable: true,
      cell: (row: PurchaseOrder) => (
        <DateCell value={row.created_at} className="text-muted-foreground" />
      ),
    },
    {
      key: "po_number",
      header: "PO #",
      sortable: true,
      cell: (row: PurchaseOrder) => (
        <div className="font-medium">{row.po_number}</div>
      ),
    },
    {
      key: "vendor",
      header: "Vendor",
      cell: (row: PurchaseOrder) => (
        <span>{row.vendor?.name || "-"}</span>
      ),
    },
    {
      key: "status",
      header: "Status",
      cell: (row: PurchaseOrder) => (
        <div className="flex items-center gap-1.5">
          <StatusBadge status={row.status} type="purchaseOrder" />
          {row.status === "archived" && (
            <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700 dark:border-amber-800 dark:text-amber-300">
              <Archive className="h-2.5 w-2.5 mr-0.5" />Archived
            </Badge>
          )}
        </div>
      ),
    },
    {
      key: "total",
      header: "Total",
      className: "text-right",
      cell: (row: PurchaseOrder) => (
        <span className="font-medium">${Number(row.total || 0).toLocaleString()}</span>
      ),
    },
    {
      key: "expected_date",
      header: "Expected",
      cell: (row: PurchaseOrder) => (
        <span className="text-muted-foreground">
          {row.expected_date ? new Date(row.expected_date).toLocaleDateString() : "-"}
        </span>
      ),
    },
    {
      key: "order_date",
      header: "Order Date",
      sortable: true,
      cell: (row: PurchaseOrder) => (
        <span className="text-muted-foreground">
          {row.order_date ? new Date(row.order_date).toLocaleDateString() : "-"}
        </span>
      ),
    },
  ];

  const purchaseOrders = data?.purchaseOrders || [];
  const total = data?.total || 0;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Purchase Orders"
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant={includeArchived ? "default" : "outline"}
              size="sm"
              onClick={() => setIncludeArchived((v) => !v)}
            >
              <Archive className="h-3.5 w-3.5 mr-1.5" />
              {includeArchived ? "Hide archived" : "Show archived"}
            </Button>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="h-9 rounded-md border border-input bg-background px-3 py-1 text-sm"
            >
              <option value="">All Statuses</option>
              {poStatusOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <input
              type="text"
              placeholder="Search purchase orders..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-9 rounded-md border border-input bg-background px-3 py-1 text-sm w-64"
            />
            <Button size="sm" onClick={() => setNewPoDrawerOpen(true)}>
              <Plus className="h-4 w-4 mr-1" />
              New PO
            </Button>
          </div>
        }
      />

      <DataTable
        data={purchaseOrders}
        columns={columns}
        isLoading={isLoading}
        onRowClick={(row) => setLocation(`/purchase-orders/${row.id}`)}
        emptyMessage="No purchase orders found"
        emptyIcon={<ClipboardList className="h-12 w-12 text-muted-foreground/50" />}
        pageSize={pageSize}
        totalCount={total}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
      />

      <FormDrawer
        open={newPoDrawerOpen}
        onOpenChange={setNewPoDrawerOpen}
        title="New Purchase Order"
        description="Create a new purchase order. You can add line items after creation."
        formId="create-purchase-order"
        isPending={createMutation.isPending}
        submitLabel="Create Purchase Order"
        size="md"
        data-testid="drawer-new-purchase-order"
      >
        <Form {...form}>
          <form
            id="create-purchase-order"
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-6"
          >
            <FormField
              control={form.control}
              name="vendorId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Vendor *</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value} disabled={vendorsLoading}>
                    <FormControl>
                      <SelectTrigger variant="flat">
                        <SelectValue placeholder="Select a vendor" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {vendors.map((vendor) => (
                        <SelectItem key={vendor.id} value={vendor.id}>
                          {vendor.name}{vendor.code ? ` (${vendor.code})` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="shipToAccountId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Ship To (optional)</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value} disabled={accountsLoading}>
                    <FormControl>
                      <SelectTrigger variant="flat">
                        <SelectValue placeholder="None (ship to warehouse)" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="__none__">None (ship to warehouse)</SelectItem>
                      {accounts.map((account) => (
                        <SelectItem key={account.id} value={account.id}>
                          {account.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="orderDate"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>Order Date</FormLabel>
                  <Popover>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          variant="outline"
                          className={cn(
                            "justify-start text-left font-normal",
                            !field.value && "text-muted-foreground",
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {field.value ? format(field.value, "PPP") : "Pick a date"}
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={field.value}
                        onSelect={field.onChange}
                      />
                    </PopoverContent>
                  </Popover>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes</FormLabel>
                  <FormControl>
                    <Textarea
                      variant="flat"
                      placeholder="Add any notes for this purchase order..."
                      className="min-h-[100px]"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </Form>
      </FormDrawer>
    </div>
  );
}
